/**
 * The acquisition funnel, as a closed set of events.
 *
 * ---------------------------------------------------------------------------
 * No new analytics platform
 * ---------------------------------------------------------------------------
 * The existing site already runs three things: Google Analytics 4
 * (`G-6WMJ0QJ2NX`), a Google Ads conversion tag (`AW-1043605786`) and PostHog,
 * which it initialises in production only. That is the infrastructure, and this
 * module writes to it rather than adding a fourth.
 *
 * ---------------------------------------------------------------------------
 * Why the event names are a union rather than a string
 * ---------------------------------------------------------------------------
 * A funnel is only readable if the same step is called the same thing on every
 * page that has it. `track('widget_selected')` on the homepage and
 * `track('widget-selected')` on a generator produce two funnels, each missing
 * half its traffic, and nothing fails — the data is just quietly wrong for as
 * long as it takes somebody to notice. The union makes a typo a build error.
 *
 * Adding an event here is deliberate: it should be a step somebody would ask a
 * question about, not a click somebody happened to instrument.
 */

export type FunnelEvent =
  /** The primary call to action, wherever it appears. */
  | 'hero_cta_clicked'
  /** A review source was chosen — on the homepage demo or an integration page. */
  | 'source_selected'
  | 'import_started'
  /**
   * A visitor asked the API to read their own listing, and what came back.
   *
   * The pair rather than one event with an outcome property, because the gap
   * between them is the number worth watching: this is the only thing on the
   * site that spends money per press, and requests that never succeed are
   * either people pasting the wrong kind of link or the deployment's daily
   * budget being exhausted — both of which want noticing quickly, and neither
   * of which shows up in a single counter.
   */
  | 'listing_read_requested'
  | 'listing_read_succeeded'
  | 'reviews_previewed'
  | 'review_selected'
  | 'widget_selected'
  /** Any change to a widget's appearance. Fired once per settled edit. */
  | 'widget_customised'
  /** The visitor has a widget they could actually use. */
  | 'generator_completed'
  /** They clicked through to make an account from a generator. */
  | 'signup_started_from_generator'
  | 'agency_cta_clicked'
  | 'comparison_cta_clicked'
  /**
   * The pricing page's calls to action, carrying which plan was clicked in
   * `surface`. Added with the page itself — `/pricing` was linked from the
   * header of every page and had no route until the launch audit.
   */
  | 'pricing_cta_clicked';

/**
 * Properties an event may carry.
 *
 * Narrow on purpose. Analytics payloads accrete keys until nobody knows which
 * are populated on which event; these are the dimensions the funnel is actually
 * sliced by — which source, which widget, which page it happened on.
 */
export type FunnelProperties = {
  source?: string;
  widget?: string;
  /** The surface the event happened on, e.g. `homepage`, `generator/carousel`. */
  surface?: string;
  competitor?: string;
  platform?: string;
};

type Gtag = (
  command: 'event',
  name: string,
  params?: Record<string, unknown>
) => void;

type PostHog = {
  capture: (name: string, properties?: Record<string, unknown>) => void;
};

declare global {
  interface Window {
    gtag?: Gtag;
    posthog?: PostHog;
  }
}

/**
 * Record a funnel step.
 *
 * Safe to call from anywhere, including during server rendering and before
 * either script has loaded: it does nothing rather than throwing. An analytics
 * call that can break a page is a bad trade — nobody visits a website for the
 * telemetry.
 */
export function track(event: FunnelEvent, properties: FunnelProperties = {}): void {
  if (typeof window === 'undefined') return;

  try {
    window.gtag?.('event', event, properties);
    window.posthog?.capture(event, properties);
  } catch {
    /*
      Swallowed deliberately, and with no console noise. Ad blockers replace
      these globals with stubs that throw, which is a condition affecting a
      large minority of visitors and telling us nothing we can act on — logging
      it would fill the console of every blocked session with a message about a
      thing working as intended.
    */
  }
}

/**
 * The Google Ads conversion, which is a separate concern from the funnel above.
 *
 * The existing site fires this from its two "Get started" buttons, with the
 * same send-to id. Kept as its own function because it is a *billing* event for
 * an ad campaign rather than a step in a funnel, and the two get confused the
 * moment they share a name.
 */
export const ADS_CONVERSION_ID = 'AW-1043605786';

/**
 * The GA4 property, and the tag that loads it.
 *
 * `track()` above writes to `window.gtag`, and until this existed nothing on
 * the rebuilt site defined it — every funnel event was a call into `undefined`,
 * swallowed by the try/catch that exists for ad blockers. The id defaults to
 * the property the previous site reported to, so the history is continuous;
 * set `VITE_GA_MEASUREMENT_ID=off` to ship without it.
 *
 * Production only. A development tab reporting into the live property is
 * noise in exactly the numbers the growth work is measured by.
 */
export const GA_MEASUREMENT_ID = (() => {
  const value = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (typeof value === 'string' && value.trim()) return value.trim();
  return 'G-6WMJ0QJ2NX';
})();

/**
 * Dub's script, for the partner programme.
 *
 * It does one thing this site cannot do server-side: when a partner link
 * arrives as `?via=<slug>` rather than through a short link, it asks Dub for
 * a click id and stores it in the `dub_id` cookie, which the dashboard and
 * the API read. `data-domains.refer` is the short-link domain configured in
 * Dub; without it the script has nothing to resolve `via` against, so the
 * script is only loaded when it is set.
 *
 * The cookie goes on the parent domain, for the same reason ours does, and
 * lives the same sixty days — the programme's window in Dub should match.
 */
export const DUB_REFER_DOMAIN = (() => {
  const value = import.meta.env.VITE_DUB_REFER_DOMAIN;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
})();

export function dubHeadScripts(
  siteHostname: string
): Record<string, string | boolean>[] {
  if (!DUB_REFER_DOMAIN) return [];
  const domain =
    siteHostname === 'reviewsjet.com' || siteHostname.endsWith('.reviewsjet.com')
      ? '.reviewsjet.com'
      : null;
  return [
    {
      src: 'https://www.dubcdn.com/analytics/script.js',
      defer: true,
      'data-domains': JSON.stringify({ refer: DUB_REFER_DOMAIN }),
      'data-cookie-options': JSON.stringify({ ...(domain ? { domain } : {}), expiresInDays: 60 }),
    },
  ];
}

export function analyticsHeadScripts(): { src?: string; async?: boolean; children?: string }[] {
  if (!import.meta.env.PROD || GA_MEASUREMENT_ID === 'off') return [];
  return [
    {
      src: `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`,
      async: true,
    },
    {
      children: [
        'window.dataLayer=window.dataLayer||[];',
        'function gtag(){dataLayer.push(arguments);}',
        'window.gtag=gtag;',
        "gtag('js',new Date());",
        `gtag('config',${JSON.stringify(GA_MEASUREMENT_ID)});`,
        `gtag('config',${JSON.stringify(ADS_CONVERSION_ID)});`,
      ].join(''),
    },
  ];
}

export function trackAdsConversion(): void {
  if (typeof window === 'undefined') return;
  try {
    window.gtag?.('event', 'conversion', { send_to: ADS_CONVERSION_ID });
  } catch {
    // As above.
  }
}
